import React, { useCallback, useMemo, useState } from 'react';
import { TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Text } from '../../../components/UI-CHAKRA/Text';
import JustifyStep from './StepJustification';
import { PrescriptionInteractionJustifyDetail, PrescriptionService } from '../service';
import { ReactComponent as CloseIcon } from '../../../assets/images/icons/mevo/close.svg';
import { IconButton } from '../../../components/UI-CHAKRA/IconButton';
import { useLoading } from '../../../helpers/useLoading';
import { toast } from '../../../helpers/toast';
import { useInteractionStore } from '../../../stores/interactionStore';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { logger } from '../../../helpers/logger';

interface JustifyInteractionsDialogProps {
  onClose(): void;
  onComplete(): void;
  interactions: PrescriptionInteractionJustifyDetail[];
}

const JustifyInteractionsDialog = (props: JustifyInteractionsDialogProps) => {
  const { onClose, onComplete } = props;
  const [loading, runWithLoading] = useLoading();

  const prescriptionId = usePrescriptionStore((store) => store.prescription?.idPrescricao);
  const { updateJustifiedInteractions } = useInteractionStore();

  const [tabIndex, setTabIndex] = useState(0);

  const onGoBack = useCallback(() => {
    if (tabIndex >= 1) {
      setTabIndex((prevState) => prevState - 1);
      return;
    }

    if (tabIndex === 0) {
      onClose();
      return;
    }

    return;
  }, [tabIndex, onClose]);

  const justifyInteraction = useCallback(
    async (interaction: PrescriptionInteractionJustifyDetail) => {
      if (!prescriptionId || !interaction.justification) return;

      try {
        await PrescriptionService.justifyInteraction(
          prescriptionId,
          interaction.prescriptionInteractionJustificationId,
          interaction.justification
        );
      } catch (e) {
        logger.error(e);
        toast.error('Erro ao justificar interação, tente novamente.');
      }
    },
    [prescriptionId]
  );

  const interactionsToJustify = useMemo(() => {
    return props.interactions.filter((interaction) => interaction.requiredAction !== 'N/A');
  }, [props.interactions]);

  const numberOfInteractions = useMemo(() => {
    return interactionsToJustify.length;
  }, [interactionsToJustify]);

  const handleOnNext = useCallback(
    async (interaction: PrescriptionInteractionJustifyDetail) => {
      runWithLoading(async () => {
        if (tabIndex < numberOfInteractions - 1) {
          if (interaction.requiredAction !== 'N/A') {
            justifyInteraction(interaction);
          }
          updateJustifiedInteractions(interaction);
          setTabIndex((prevState) => prevState + 1);
          return;
        }

        // ultimo step
        if (interaction.requiredAction !== 'N/A') {
          justifyInteraction(interaction);
        }
        updateJustifiedInteractions(interaction);

        onComplete();
        return;
      });
    },
    [tabIndex, numberOfInteractions, updateJustifiedInteractions, onComplete, runWithLoading, justifyInteraction]
  );

  return (
    <Box width={'100%'} h={'100%'} display={'flex'} flexDirection={'column'}>
      <Box w={'100%'} bg={'#fff'} borderRadius={'20px'} mb={'8px'} p={'24px'}>
        <Text fontFamily={'Ambit-Regular'} fontWeight={700} fontSize={'18px'} mb={'10px'} lineHeight={'18px'}>
          Interação Medicamentosa
        </Text>
        <Text fontFamily={'Ambit-Regular'} fontWeight={400} fontSize={'14px'} lineHeight={'17px'}>
          Existem medicamentos com interação e ou contraindicação na receita.
        </Text>

        <IconButton
          position={'absolute'}
          right={'30px'}
          top={'30px'}
          borderRadius={'50%'}
          variant={'ghost'}
          aria-label="close"
          onClick={onClose}
          data-testid={'close'}
          size={'sm'}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box
        w={'100%'}
        bg={'#fff'}
        borderRadius={'20px'}
        p={'24px'}
        display={'flex'}
        flexDir={'column'}
        justifyContent={'space-between'}
      >
        <Tabs isLazy index={tabIndex} variant={'unstyled'} onChange={() => {}}>
          <TabPanels>
            {interactionsToJustify.map((interaction) => {
              return (
                <TabPanel p={0} key={interaction.prescriptionInteractionJustificationId}>
                  <JustifyStep
                    key={interaction.prescriptionInteractionJustificationId}
                    currentStep={tabIndex + 1}
                    numberOfSteps={interactionsToJustify.length}
                    interaction={interaction}
                    goBack={onGoBack}
                    next={handleOnNext}
                    isLoading={loading}
                  />
                </TabPanel>
              );
            })}
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
};

export default JustifyInteractionsDialog;
